<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          <template v-if="!userIsCustomer">{{ $t('customer.customers') }}</template>
          <template v-else>{{ $t('customer.profile') }}</template>
        </span>
        <h3 class="page-title">
          <template v-if="!userIsCustomer">{{ $t('customer.edit') }}</template>
          <template v-else>{{ $t('customer.yourAccount') }}</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="!userIsCustomer"
          theme="secondary"
          @click.prevent="$router.push('/klienci')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="customer-edit-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            v-sticky-tabs
            :nav-class="allDataLoaded ? validationResults : ''"
            card>
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                <span class="mr-auto">{{ $t('ui.generalInfo') }}</span>

                <d-button
                  v-if="!userIsCustomer && !force_update_data_at"
                  theme="danger"
                  outline
                  class="ml-2"
                  :disabled="forceUpdateDataInSoftlabInProgress"
                  @click.prevent="forceUpdateDataInSoftlab">
                  <template v-if="forceUpdateDataInSoftlabInProgress">{{ $t('softlab.updatingData') }}</template>
                  <template v-else>{{ $t('softlab.forceSoftlabDataUpdate') }}</template>
                </d-button>

                <d-button
                  v-if="!userIsCustomer"
                  theme="primary"
                  class="ml-2"
                  @click.prevent="showCustomerOrders()">
                  {{ $t('customer.showCustomerOrders') }}
                </d-button>

                <d-button
                  v-if="!userIsCustomer"
                  theme="secondary"
                  class="ml-2"
                  @click.prevent="showCustomerOffers()">
                  {{ $t('customer.showCustomerOffers') }}
                </d-button>
              </h4>

              <d-row>
                <d-col :cols="userIsCustomer ? 12 : 4">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.companyName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="!userIsCustomer"
                  cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.dayForPayment') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="payment_days"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="payment_days" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="!userIsCustomer"
                  cols="6"
                  class="d-flex">
                  <div class="form-group w-50">
                    <label class="d-block mb-0">
                      <d-checkbox inline v-model="send_communication" toggle toggle-small  class="mb-0">
                        {{ $t('customer.sendCommunication') }}
                      </d-checkbox>
                    </label>

                    <label class="d-block mb-0">
                      <d-checkbox inline v-model="send_stock_report" toggle toggle-small  class="mb-0">
                        {{ $t('customer.sendReportFromStock') }}
                      </d-checkbox>
                    </label>

                    <label class="d-block mb-0">
                      <d-checkbox inline v-model="hide_customer_in_qr" toggle toggle-small class="mb-0">
                        {{ $t('customer.hidecustomerInQR') }}
                      </d-checkbox>
                    </label>
                  </div>

                  <div class="form-group w-50">
                    <label class="d-block mb-0">
                      <d-checkbox inline v-model="exclude_from_invoice_reminder" toggle toggle-small  class="mb-0">
                        {{ $t('customer.excludeFromInvoiceReminder') }}
                      </d-checkbox>
                    </label>

                    <label class="d-block mb-0 align-middle">
                      <d-checkbox inline v-model="auto_special_requirement" toggle toggle-small class="mb-0 mr-1">
                        {{ $t('customer.autoSpecialRequirements') }}
                      </d-checkbox>
                    </label>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="!userIsCustomer">
                <d-col cols="1">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.supplierAlt') }}:
                    </label>

                    <div>
                      <d-checkbox
                        inline
                        v-model="is_supplier"
                        toggle
                        toggle-small />
                    </div>
                  </div>
                </d-col>

                <d-col :cols="is_supplier ? 4 : 7">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.supplierProductsReminderEmail') }}:
                    </label>

                    <validation-provider
                      rules="email"
                      name="email"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="email"
                        v-model="products_reminder_email"
                        :readonly="!is_supplier" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <d-row>
                    <d-col cols="6">
                      <div class="form-group">
                        <label>
                          {{ $t('ui.sendPurchaseInvoiceEmail') }}:
                        </label>

                        <div>
                          <d-checkbox
                            inline
                            v-model="send_purchase_invoice_email"
                            toggle
                            toggle-small
                            :disabled="!is_supplier" />
                        </div>
                      </div>
                    </d-col>
                    <d-col cols="6">
                      <div class="form-group">
                        <label>
                          {{ $t('ui.purchaseInvoiceEmail') }}:
                        </label>

                        <d-input
                          type="email"
                          v-model="purchase_invoice_email"
                          :readonly="!is_supplier || !send_purchase_invoice_email" />
                      </div>
                    </d-col>
                  </d-row>
                </d-col>

                <d-col
                  v-if="is_supplier"
                  cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('purchase.purchaseCategory') }}:
                    </label>

                    <d-form-select
                      v-model="purchase_category"
                      :options="[{
                        text: $t('welcome.choose'),
                        value: null
                      }, {
                        text: $t('purchase.purchaseType.domestic'),
                        value: 'DOMESTIC'
                      }, {
                        text: $t('purchase.purchaseType.import'),
                        value: 'IMPORT'
                      }]" />
                  </div>

                  <div class="form-group">
                    <label for="name">
                      {{ $t('purchase.warehouse') }}:
                    </label>

                    <d-form-select
                      v-model="warehouse"
                      :options="[{
                        text: $t('welcome.choose'),
                        value: null
                      }, {
                        text: 'MG02',
                        value: 'MG02'
                      }, {
                        text: 'SE01',
                        value: 'SE01'
                      }, {
                        text: 'DE01',
                        value: 'DE01'
                      }]" />
                  </div>
                </d-col>

                <d-col
                  v-if="auto_special_requirement"
                  cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('order.isSpecialRequirement') }}:
                    </label>

                    <d-textarea
                      v-model="special_requirement"
                      :value.prop="special_requirement"
                      :rows="5" />
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.newsletterAgreement') }}:
                    </label>

                    <span
                      :class="{
                        'text-success': newsletter_status === 'SUBSCRIBED',
                        'text-danger': newsletter_status === 'UNSUBSCRIBED'
                      }">
                      <template v-if="newsletter_status === 'SUBSCRIBED'">
                        {{ $t('customer.newsletterStatus.subscribed') }}
                      </template>
                      <template v-if="newsletter_status === 'UNSUBSCRIBED'">
                        {{ $t('customer.newsletterStatus.unsubscribed') }}
                      </template>
                      <template v-if="newsletter_status === null">
                        {{ $t('customer.newsletterStatus.null') }}
                      </template>

                      <div>
                        <d-checkbox inline v-model="mailing_consent" toggle toggle-small />
                      </div>
                    </span>
                  </div>
                </d-col>
                <d-col cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.newsletterStatusAt') }}:
                    </label>
                    <div>
                      <template v-if="newsletter_status_at">
                        {{ newsletter_status_at }}
                      </template>
                      <template v-else>
                        &mdash;
                      </template>
                    </div>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col
                  v-if="!userIsCustomer"
                  cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.customerCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code"
                        :disabled="!userIsAdmin" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="userIsCustomer ? 4 : 3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.language') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="language_id"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          :options="languageOptions"
                          v-model="language_id"
                          :searchable="false"
                          :clearable="false"
                          :reduce="language => language.value"
                          :placeholder="$t('settings.language.chooseLanguage')"
                          label="text">
                          <template slot="no-options">
                            {{ $t('settings.language.noLanguagesAvailable') }}&hellip;
                          </template>
                        </v-select>
                      </div>

                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="userIsCustomer ? 4 : 3">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.currency.currency') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="currency_id"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          :options="currencyOptions"
                          v-model="currency_id"
                          :searchable="false"
                          :clearable="false"
                          :reduce="currency => currency.value"
                          :placeholder="$t('settings.currency.chooseCurrency')"
                          label="text">
                          <template slot="no-options">
                            {{ $t('settings.currency.noCurrenciesAvailable') }}&hellip;
                          </template>
                        </v-select>
                      </div>

                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col :cols="userIsCustomer ? 4 : 3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.shipper') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="shipper_id"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          :options="shipperOptions"
                          v-model="shipper_id"
                          :searchable="false"
                          :clearable="false"
                          :reduce="shipper => shipper.value"
                          :placeholder="$t('shippers.chooseShipper')"
                          label="text">
                          <template slot="no-options">
                            {{ $t('shippers.noShippersAvailable') }}&hellip;
                          </template>
                        </v-select>
                      </div>

                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.country.country') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="country-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="country_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="countriesOptions"
                        @change="countryChange" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.regions.region') }}:
                    </label>

                    <validation-provider
                      rules="non_zero"
                      name="region-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="region_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="regionsOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div
                    v-if="!userIsCustomer"
                    class="form-group">
                    <label>
                      {{ $t('customer.sourceOfGain') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="source-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="source_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="sourcesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="userIsAdmin"
                  cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('agents.selectAgent') }}:
                    </label>

                    <select-agent
                      v-if="allDataLoaded"
                      :agentID="agent_id" />
                  </div>
                </d-col>
              </d-row>

              <div class="form-group">
                <label>
                  {{ $t('customer.customerLogo') }}:
                </label>

                <div v-if="logo_url" class="customer-logo">
                  <img :src="logo_url" />
                </div>

                <div v-if="logo_url">
                  <a
                    href="#"
                    @click.prevent="removeCustomerLogo()"
                    class="text-danger">
                    <small>{{ $t('customer.removeLogo') }}</small>
                  </a>
                </div>

                <file-input
                  v-if="!logo_url"
                  :onChange="setCustomerLogo"
                  id="inputFileCustomerLogo" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.emailAddress') }}:
                </label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('customer.telephone') }}:
                </label>

                <d-input
                  v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                  v-model="phone" />
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label>
                  {{ $t('customer.termsOfSale') }}:
                </label>

                <d-textarea
                  v-model="terms_of_sale"
                  :value.prop="terms_of_sale" />
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label>
                  {{ $t('customer.termsOfSaleIndent') }}:
                </label>

                <d-textarea
                  v-model="terms_of_sale_indent"
                  :value.prop="terms_of_sale_indent" />
              </div>

              <div
                v-if="!userIsCustomer"
                class="form-group">
                <label>
                  {{ $t('settings.customerTags.customerTags') }}:
                </label>

                <ul class="customer-tags-list">
                  <li
                    v-for="(tag, index) of customerTags"
                    :key="'customer-tag-' + index">
                    <d-checkbox
                      inline
                      v-model="tags[tag.id]">
                      <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
                        {{ tag.name }}
                      </d-badge>
                    </d-checkbox>
                  </li>
                </ul>
              </div>
            </d-tab>

            <d-tab :title="$t('customer.correspondenceInfo')">
              <div
                v-if="invoiceDataNotFromGUS"
                class="form-group d-flex">
                <d-button
                  theme="success"
                  :outline="true"
                  class="ml-auto mr-auto"
                  @click.prevent="setInvoiceDataAsCorrespondenceData">
                  {{ $t('customer.setInvoiceDataAsCorrespondenceData') }}
                </d-button>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('settings.country.country') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="correspondence-country-id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="correspondence_country_id"
                    :state="errors.length ? 'invalid' : null"
                    :options="countriesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.nameAndSurename') }}:
                </label>

                <d-input
                  v-model="correspondence_name" />
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.companyName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="correspondence-company-name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="correspondence_company_name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col cols="12" md="10" lg="10">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.city') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="correspondence-city"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="correspondence_city" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.zipCode') }}:
                    </label>

                    <validation-provider
                      :rules="polandCountryID === correspondence_country_id ? 'required|postal_code_pl' : 'required'"
                      name="correspondence-postcode"
                      v-slot="{ errors }">
                      <d-input
                        v-if="polandCountryID === correspondence_country_id"
                        key="correspondence-postcode-mask"
                        v-mask="'##-###'"
                        :state="errors.length ? 'invalid' : null"
                        v-model="correspondence_postcode" />
                      <d-input
                        v-else
                        key="correspondence-postcode-nomask"
                        :state="errors.length ? 'invalid' : null"
                        v-model="correspondence_postcode" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="8" lg="8">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.street') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="correspondence-street"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="correspondence_street" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.buildingNumber') }}:
                    </label>

                    <d-input v-model="correspondence_building" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.houseNumber') }}:
                    </label>

                    <d-input v-model="correspondence_locale" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab :title="$t('customer.shippingInfo')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('customer.shippingInfo') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addAddress">
                  <i class="material-icons">add</i>
                  {{ $t('customer.addAddress') }}
                </d-button>
              </h4>

              <d-row class="px-3 pb-4">
                <div class="d-flex w-100">
                  <d-input
                    v-model="addressFilter"
                    :placeholder="$t('customer.searchAddressesByCompanyNameAddressOrContactInfo')" />
                  <d-button
                    v-if="addressFilter"
                    @click.prevent="addressFilter = ''"
                    theme="danger"
                    outline
                    class="ml-2">
                    {{ $t('welcome.clear') }}
                  </d-button>
                </div>
                <small class="w-100 pt-2">
                  {{ $t('customer.fountAddresses') }} <strong>{{ filteredAddresses.length }}</strong>.<br>
                  {{ $t('customer.maxAddressSearchResults') }}
                </small>
              </d-row>

              <table class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('customer.company') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.address') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.person') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="loadedAddresses">
                  <tr
                    v-for="(address, index) of filteredAddresses"
                    :key="'address-' + index">
                    <td class="align-middle">
                      {{ address.company_name }}
                    </td>
                    <td class="align-middle">
                      {{ getCountry(address.country_id) }}<br>
                      {{ address.street }} <template v-if="address.building">{{ address.building }} / {{ address.locale }}</template><br>
                      {{ address.postcode }} {{ address.city }}
                    </td>
                    <td class="align-middle">
                      {{ address.name }}<br>
                      {{ address.email }}<br>
                      {{ address.phone }}
                    </td>
                    <td
                      class="align-middle"
                      style="width: 200px;">
                      <d-button
                        theme="primary"
                        @click.prevent="editAddress(address)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        v-if="!userIsCustomer"
                        theme="danger"
                        :disabled="removeAddressInProgress"
                        @click.prevent="removeAddress(address.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                  <tr v-if="addresses.length === 0">
                    <td
                      colspan="4"
                      class="text-center">
                      {{ $t('customer.noAddressesToDisplay') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!loadedAddresses" class="pb-3 pt-3 text-center">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>

              <d-alert
                v-if="loadAddressesError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab
              v-if="!userIsCustomer"
              :title="$t('customer.invoiceInfo')">
              <d-row>
                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('settings.country.countryCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-country-prefix"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="invoice_country_prefix"
                        :state="errors.length ? 'invalid' : null"
                        :options="countriesCodesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12" md="8" lg="8">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.vatIDNumber') }}:
                    </label>

                    <div class="d-flex">
                      <validation-provider
                        rules="required"
                        name="invoice-tax-number"
                        class="w-100"
                        v-slot="{ errors }">
                        <d-input
                          :state="errors.length ? 'invalid' : null"
                          v-mask="'XXXXXXXXXXXXXXX'"
                          v-model="invoice_tax_number" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                        <small
                          v-if="latest_verified_tax_number && invoice_tax_number && (invoice_country_prefix + '' + invoice_tax_number !== latest_verified_tax_number)"
                          class="text-warning">
                          {{ $t('customer.taxNumberDifference') }} ({{ latest_verified_tax_number }})
                        </small>
                      </validation-provider>

                      <d-button
                        v-if="can_manually_verify_vies || exceptionOccurred"
                        theme="primary"
                        class="ml-2 w-25"
                        @click.prevent="manuallyVerifyVies"
                        :disabled="manualCheckingTaxNumber">
                        <div
                          v-if="manualCheckingTaxNumber"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        {{ $t('customer.verifyManually') }}
                      </d-button>
                    </div>
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2" class="pt-4">
                  <d-button
                    v-if="nonVerifiedCountryPrefixes.indexOf(invoice_country_prefix) === -1"
                    theme="success"
                    :disabled="checkingTaxNumber"
                    class="mt-2"
                    @click.prevent="verifyCustomerTaxID">
                    <div
                      v-if="checkingTaxNumber"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <template v-if="invoice_country_prefix === 'PL'">{{ $t('customer.getDataFromGUS') }}</template>
                    <template v-else>{{ $t('customer.getDataFromVIES') }}</template>
                  </d-button>

                  <template v-if="nonVerifiedCountryPrefixes.indexOf(invoice_country_prefix) === -1">
                    <small
                      v-if="invoice_country_prefix === 'PL' && !gusEnabled"
                      class="text-danger d-block">
                      {{ $t('settings.integrations.integrationIsDisabled') }}
                    </small>

                    <small
                      v-if="invoice_country_prefix !== 'PL' && !viesEnabled"
                      class="text-danger d-block">
                      {{ $t('settings.integrations.integrationIsDisabled') }}
                    </small>
                  </template>
                </d-col>
              </d-row>

              <d-row v-if="tax_number_verified_at">
                <d-col cols="12" class="pb-3">
                  <small>
                    {{ $t('customer.lastTaxVerification') }}
                    <span class="text-primary">
                      {{ tax_number_verified_at }}
                    </span>
                    <span class="px-2"> | </span>
                    {{ $t('customer.retrievedStatus') }}
                    <span
                      v-if="tax_number_verified_status === 'VALID'"
                      class="text-success">
                      {{ $t('customer.validTaxNumber') }}
                    </span>
                    <span
                      v-if="tax_number_verified_status === 'INVALID'"
                      class="text-danger">
                      {{ $t('customer.invalidTaxNumber') }}
                    </span>
                    <template v-if="latest_verified_tax_number">
                      ({{ $t('customer.retrievedStatusFor') }}{{ latest_verified_tax_number }})
                    </template>

                    <template v-if="latest_verified_tax_number && latest_verified_tax_number.substr(0, 2) !== 'PL' && tax_number_verified_status === 'VALID'">
                      <span class="px-2"> | </span>
                      <template v-if="!checkedTaxNumberAgain">
                        <a
                          v-if="!downloadingTaxIDConfirmationPdf"
                          href="#download-tax-info"
                          @click.prevent="downloadTaxNumberConfirmation">
                          <i class="material-icons">cloud_download</i>
                          {{ $t('customer.downloadTaxNumberConfirmation') }}
                        </a>
                        <span v-else>
                          <div class="spinner-border spinner-border-sm" role="status"></div>
                          {{ $t('customer.downloadTaxNumberConfirmationDownloading') }}
                        </span>
                      </template>
                      <span v-else>
                        {{ $t('customer.downloadTaxNumberConfirmationAfterSave') }}
                      </span>
                    </template>

                    <template>
                      <span class="px-2"> | </span>
                      {{ $t('customer.vatIDActiveDuringCheck') }}:
                      <d-badge
                        v-if="tax_number_verified_is_active"
                        theme="success">
                        {{ $t('ui.yes') }}
                      </d-badge>
                      <d-badge
                        v-if="!tax_number_verified_is_active"
                        theme="danger">
                        {{ $t('ui.no') }}
                      </d-badge>
                    </template>
                  </small>
                </d-col>
              </d-row>

              <d-row>
                <d-col :cols="invoice_receiver !== 'CUSTOM_EMAIL' ? 12 : 6">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.invoiceReceiver') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-receiver"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="invoice_receiver"
                        :state="errors.length ? 'invalid' : null"
                        :options="[{
                          'text': $t('welcome.choose'),
                          'value': null
                        }, {
                          'text': $t('users.customer'),
                          'value': 'CUSTOMER'
                        }, {
                          'text': $t('customer.contactInsideOrder'),
                          'value': 'ORDER_CONTACT'
                        }, {
                          'text': $t('ui.emailAddress'),
                          'value': 'CUSTOM_EMAIL'
                        }]">
                      </d-form-select>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col
                  v-if="invoice_receiver === 'CUSTOM_EMAIL'"
                  cols="6">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.invoiceReceiverEmailAddress') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-email"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_email" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.invoiceName') }}
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="10" lg="10">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.city') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-city"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_city" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.zipCode') }}:
                    </label>

                    <validation-provider
                      :rules="invoice_country_prefix === 'PL' ? 'required|postal_code_pl' : 'required'"
                      name="invoice-postcode"
                      v-slot="{ errors }">
                      <d-input
                        v-if="invoice_country_prefix === 'PL'"
                        key="invoice-postcode-mask"
                        v-mask="'##-###'"
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_postcode" />
                      <d-input
                        v-else
                        key="invoice-postcode-nomask"
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_postcode" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="12" md="8" lg="8">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.street') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="invoice-street"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="invoice_street" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.buildingNumber') }}:
                    </label>

                    <d-input v-model="invoice_building" />
                  </div>
                </d-col>

                <d-col cols="12" md="2" lg="2">
                  <div class="form-group">
                    <label>
                      {{ $t('customer.houseNumber') }}:
                    </label>

                    <d-input v-model="invoice_locale" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <d-tab
              v-if="!userIsCustomer"
              :title="$t('customer.discount.discounts')">
              <div class="form-group">
                <label>
                  {{ $t('customer.discount.serviceDiscount') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0|max_value:100"
                  name="discount-services"
                  v-slot="{ errors }">
                  <d-input-group
                    prepend="%"
                    :class="{'mb-2': true, 'is-invalid': errors.length}">
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="discount_services" />
                    <d-input-group-text slot="append">%</d-input-group-text>
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('customer.discount.shippingDiscount') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0|max_value:100"
                  name="discount-shipping"
                  v-slot="{ errors }">
                  <d-input-group
                    prepend="%"
                    :class="{'mb-2': true, 'is-invalid': errors.length}">
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="discount_shipping" />
                    <d-input-group-text slot="append">%</d-input-group-text>
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('customer.discount.productDiscount') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0|max_value:100"
                  name="discount-products"
                  v-slot="{ errors }">
                  <d-input-group
                    prepend="%"
                    :class="{'mb-2': true, 'is-invalid': errors.length}">
                    <input
                      :class="{
                        'form-control': true,
                        'is-invalid': errors.length
                      }"
                      :invalid="errors.length ? true : null"
                      type="number"
                      v-model="discount_products" />
                    <d-input-group-text slot="append">%</d-input-group-text>
                  </d-input-group>
                  <d-form-invalid-feedback>
                    {{ errors[0] }}
                  </d-form-invalid-feedback>
                </validation-provider>
              </div>

              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="indent_discount_enabled"
                  toggle
                  toggle-small
                  class="mb-0">
                  {{ $t('customer.indentDiscountEnabled') }}
                </d-checkbox>
              </div>
            </d-tab>

            <d-tab :title="$t('customer.contactPeople')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('customer.contactPeople') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addContactPerson">
                  <i class="material-icons">add</i>
                  {{ $t('customer.addContactPerson') }}
                </d-button>
              </h4>

              <table class="table mt-0 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0">{{ $t('ui.nameAndSurename') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.emailAddress') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.telephone') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.stockReportsNotifications') }}</th>
                    <th scope="col" class="border-0">{{ $t('customer.newsletterAgreement') }}</th>
                    <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="loadedContactPersons">
                  <tr
                    v-for="(contactPerson, index) of contactPersons"
                    :key="'contact-person-' + index">
                    <td class="align-middle">
                      {{ contactPerson.name }}
                    </td>
                    <td class="align-middle">
                      {{ contactPerson.email }}
                    </td>
                    <td class="align-middle">
                      {{ contactPerson.phone }}
                    </td>
                    <td class="align-middle">
                      <span
                        v-if="contactPerson.exclude_from_stock_report"
                        class="text-danger">
                        {{ $t('ui.no') }}
                      </span>
                      <span
                        v-else
                        class="text-success">
                        {{ $t('ui.yes') }}
                      </span>
                    </td>
                    <td class="align-middle">
                      <span
                        :class="{
                          'text-success': contactPerson.newsletter_status === 'SUBSCRIBED',
                          'text-danger': contactPerson.newsletter_status === 'UNSUBSCRIBED'
                        }">
                        <template v-if="contactPerson.newsletter_status === 'SUBSCRIBED'">
                          {{ $t('customer.newsletterStatus.subscribed') }}
                        </template>
                        <template v-if="contactPerson.newsletter_status === 'UNSUBSCRIBED'">
                          {{ $t('customer.newsletterStatus.unsubscribed') }}
                        </template>
                        <template v-if="contactPerson.newsletter_status === null">
                          {{ $t('customer.newsletterStatus.null') }}
                        </template>
                      </span>
                    </td>
                    <td
                      class="align-middle"
                      style="width: 200px;">
                      <d-button
                        theme="primary"
                        @click.prevent="editContactPerson(contactPerson)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        v-if="!userIsCustomer"
                        theme="danger"
                        :disabled="removeContactPersonInProgress"
                        @click.prevent="removeContactPerson(contactPerson.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                  <tr v-if="contactPersons.length === 0">
                    <td
                      colspan="6"
                      class="text-center">
                      {{ $t('customer.noContactPeopleToDisplay') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="!loadedContactPersons" class="pb-3 pt-3 text-center">
                {{ $t('ui.dataLoading') }}&hellip;
              </div>

              <d-alert
                v-if="loadContactPersonsError"
                show
                theme="warning">
                {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
              </d-alert>
            </d-tab>

            <d-tab
              v-if="!userIsCustomer"
              :title="$t('settings.transaltion.translations')">
              <h4 class="pb-3 mb-0 border-bottom d-flex">
                {{ $t('settings.transaltion.translations') }}

                <d-button
                  class="ml-auto"
                  theme="success"
                  @click.prevent="addTranslation">
                  <i class="material-icons">add</i>
                  {{ $t('settings.transaltion.addTranslation') }}
                </d-button>
              </h4>

              <table class="table mt-2 mb-0 full-width">
                <thead class="bg-light">
                  <tr>
                    <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                    <th scope="col" class="border-0" style="width: calc(50% - 350px);">{{ $t('customer.termsOfSale') }}</th>
                    <th scope="col" class="border-0" style="width: calc(50% - 350px);">{{ $t('customer.termsOfSaleIndent') }}</th>
                    <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                  </tr>
                </thead>
                <tbody v-if="customer_translations.length">
                  <tr
                    v-for="(translation, index) of customer_translations"
                    :key="'price-' + index">
                    <td class="align-middle">
                      {{ getLanguageByID(translation.language_id) }}
                    </td>
                    <td class="align-middle">
                      {{ translation.terms_of_sale }}
                    </td>
                    <td class="align-middle">
                      {{ translation.terms_of_sale_indent }}
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="primary"
                        @click.prevent="editTranslation(translation)"
                        class="mr-2"
                        :title="$t('ui.edit')">
                        <i class="material-icons">edit</i>
                      </d-button>
                      <d-button
                        theme="danger"
                        :disabled="removeTranslationInProgress"
                        @click.prevent="removeTranslation(translation.id)"
                        :title="$t('ui.delete')">
                        <i class="material-icons">delete</i>
                      </d-button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="4" class="text-center">
                      {{ $t('customer.noTranslationsForThisClient') }}&hellip;
                    </td>
                  </tr>
                </tbody>
              </table>
            </d-tab>

            <!-- Kontakty z klientem -->
            <d-tab
              v-if="!userIsCustomer"
              :title="$t('customer.contactsWithCustomer')">
              <contacts-list
                :customerID="parseInt(this.$route.params.id, 10)"
                :isCustomerView="true" />
            </d-tab>

            <d-tab
              v-if="userIsCustomer"
              :title="$t('customer.passowrdChange')">
              <h4 class="pb-3 mb-4 border-bottom d-flex">
                {{ $t('customer.changeYourPassword') }}
              </h4>

              <validation-observer ref="customer-password-change" tag="form">
                <div class="form-group">
                  <label for="email">
                    {{ $t('login.password') }}:
                  </label>

                  <validation-provider
                    rules="min:6"
                    name="password"
                    v-slot="{ errors }">
                    <d-input
                      type="password"
                      :state="errors.length ? 'invalid' : null"
                      v-model="password" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="email">
                    {{ $t('ui.repeatPassword') }}:
                  </label>

                  <validation-provider
                    rules="password:password"
                    name="password-confirmation"
                    v-slot="{ errors }">
                    <d-input
                      type="password"
                      :state="errors.length ? 'invalid' : null"
                      v-model="passwordConfirmation" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <d-button
                    theme="success"
                    outline
                    :disabled="passwordChangeInProgress"
                    @click.prevent="validatePasswordChangeForm">
                    <template v-if="!passwordChangeInProgress">{{ $t('customer.changePassword') }}</template>
                    <template v-else>
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </template>
                  </d-button>
                </div>
              </validation-observer>
            </d-tab>

            <d-tab :title="$t('price.pricesLists')">
              <d-row>
                <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
                  <h4 class="mb-3 pb-3">
                    {{ $t('price.pricesLists') }}
                  </h4>
                </d-col>
                <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-start">
                  <d-button
                    @click.prevent="openImportPopup"
                    theme="primary"
                    class="mr-2">
                    <i class="material-icons">
                      cloud_upload
                    </i>
                    {{ $t('price.importPrices') }}
                  </d-button>

                  <d-button
                    @click.prevent="openExportPopup"
                    theme="secondary"
                    class="mr-2">
                    <i class="material-icons">
                      cloud_download
                    </i>
                    {{ $t('price.exportPrices') }}
                  </d-button>

                  <d-button
                    theme="success"
                    @click.native.prevent="openPricePopup()">
                    <i class="material-icons">add</i>
                    {{ $t('price.addNewPrice') }}
                  </d-button>
                </d-col>
              </d-row>

              <customer-prices
                :customer-id="customerID" />
            </d-tab>

            <d-tab :title="$t('customer.history')">
              <customer-editions :customerID="customerID" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center  overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <customer-address-new-popup
      v-if="showAddressAddPopup"
      :countries="countries"
      :customerID="this.id" />

    <customer-address-edit-popup
      v-if="showAddressEditPopup"
      :customerID="this.id"
      :countries="countries"
      :addressData="selectedAddress" />

    <customer-contact-person-new-popup
      v-if="showContactPersonAddPopup"
      :customerID="this.id" />

    <customer-contact-person-edit-popup
      v-if="showContactPersonEditPopup"
      :customerID="this.id"
      :contactPersonData="selectedContactPerson" />

    <customer-translation-new-popup
      v-if="showAddTranslationPopup"
      :customerID="this.id"
      :languages="languages" />

    <customer-translation-edit-popup
      v-if="showEditTranslationPopup"
      :customerID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />

    <contact-popup
      v-if="showOrderContactPopup"
      :customerID="parseInt(this.$route.params.id, 10)" />

    <contact-edit-popup
      v-if="showOrderContactEditPopup"
      :contactID="selectedContactID" />

    <customer-prices-export-popup
      v-if="showPricesExportPopup"
      :customer-id="customerID" />

    <customer-prices-import-popup
      v-if="showPricesImportPopup"
      :customer-id="customerID" />

    <customer-price-popup
      v-if="showPricePopup"
      :price-data="selectedPrice"
      :products="products"
      :customer-id="customerID" />

    <customer-orders-popup
      v-if="showCustomerOrdersPopup"
      :showAllUI="true"
      :popupContent="ordersPopupContent"
      :customerID="customerID" />
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import Contacts from './../../views/Contacts.vue';
import CustomerAddressNewPopup from './CustomerAddressNewPopup.vue';
import CustomerAddressEditPopup from './CustomerAddressEditPopup.vue';
import CustomerContactPersonNewPopup from './CustomerContactPersonNewPopup.vue';
import CustomerContactPersonEditPopup from './CustomerContactPersonEditPopup.vue';
import CustomerEditions from './CustomerEditions.vue';
import CustomerOrdersPopup from './CustomerOrdersPopup.vue';
import CustomerPrices from './CustomerPrices.vue';
import CustomerPricePopup from './CustomerPricePopup.vue';
import CustomerPricesExportPopup from './CustomerPricesExportPopup.vue';
import CustomerPricesImportPopup from './CustomerPricesImportPopup.vue';
import CustomerTranslationNewPopup from './CustomerTranslationNewPopup.vue';
import CustomerTranslationEditPopup from './CustomerTranslationEditPopup.vue';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import ContactPopup from './../contacts/ContactPopup.vue';
import ContactEditPopup from './../contacts/ContactEditPopup.vue';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import SelectAgent from './../common/SelectAgent.vue';
import { mapGetters } from 'vuex';
import Vue from 'vue';

export default {
  name: 'customer-edit',
  mixins: [
    CustomerPanel
  ],
  components: {
    'customer-address-new-popup': CustomerAddressNewPopup,
    'customer-address-edit-popup': CustomerAddressEditPopup,
    'customer-contact-person-new-popup': CustomerContactPersonNewPopup,
    'customer-contact-person-edit-popup': CustomerContactPersonEditPopup,
    'customer-translation-new-popup': CustomerTranslationNewPopup,
    'customer-translation-edit-popup': CustomerTranslationEditPopup,
    'contact-popup': ContactPopup,
    'contact-edit-popup': ContactEditPopup,
    'contacts-list': Contacts,
    'customer-editions': CustomerEditions,
    'customer-orders-popup': CustomerOrdersPopup,
    'customer-prices': CustomerPrices,
    'customer-price-popup': CustomerPricePopup,
    'customer-prices-export-popup': CustomerPricesExportPopup,
    'customer-prices-import-popup': CustomerPricesImportPopup,
    'select-agent': SelectAgent
  },
  computed: {
    ...mapGetters([
      'getUserID'
    ]),
    allDataLoaded () {
      return this.loadedData && this.loadedSources && this.loadedCountries && this.loadedLanguages && this.loadedCurrencies && this.loadedShippers && this.loadedCustomerTags && this.loadedProducts && this.loadedIntegrations && this.loadedRegions;
    },
    countriesOptions () {
      return CommonGetters.getCountriesForSelect(this.countries);
    },
    countriesCodesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesCodesForSelect(this.countries));
    },
    sourcesOptions () {
      return CommonGetters.getSourcesForSelect(this.sources);
    },
    regionsOptions () {
      return [{ value: 0, text: this.$t('settings.regions.selectRegion') }].concat(this.regions.map(region => ({
        value: region.id,
        text: region.name
      })));
    },
    validationResults () {
      let errors = this.$refs['customer-edit-form'].errors;
      let errorTabs = [];
      let results = [
        ['error-1', ['name', 'source-id', 'region-id', 'phone', 'email', 'country-id', 'code', 'shipper_id', 'payment_days']],
        ['error-2', ['correspondence-country-id', 'correspondence-company-name', 'correspondence-city', 'correspondence-postcode', 'correspondence-street']],
        ['error-4', ['invoice-name', 'invoice-receiver', 'invoice-country-prefix', 'invoice-email', 'invoice-tax-number', 'invoice-city', 'invoice-postcode', 'invoice-street']],
        ['error-5', ['discount-services', 'discount-shipping', 'discount-products']]
      ];

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    },
    languageOptions () {
      return this.languages.map(language => ({
        value: language.id,
        text: language.code
      }));
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    shipperOptions () {
      return this.shippers.map(shipper => ({
        value: shipper.id,
        text: shipper.name
      }));
    },
    filteredAddresses () {
      if (!this.addressFilter) {
        return this.addresses.slice(0, 50);
      }

      return this.addresses.filter(address => ([address.company_name, address.street, address.building, address.locale, address.postcode, address.city, address.name, address.email, address.phone].join(' ')).toLocaleLowerCase().indexOf(this.addressFilter) > -1).slice(0, 50);
    },
    customerID () {
      if (this.userIsCustomer) {
        return parseInt(this.getUserID, 10);
      }

      return parseInt(this.$route.params.id, 10);
    },
    polandCountryID () {
      let countryPL = this.countries.filter(country => country.code === 'PL');

      if (countryPL[0]) {
        return countryPL[0].id;
      }

      return false;
    },
    nonVerifiedCountryPrefixes () {
      return this.countries.filter(country => country.verify_tax_number === 0).map(country => country.code);
    },
    gusEnabled () {
      if (this.integrations.length) {
        let gusItem = this.integrations.filter(integration => integration.type === 'GUS_REGON');

        if (gusItem[0]) {
          return gusItem[0].enabled === 1;
        }
      }

      return false;
    },
    viesEnabled () {
      if (this.integrations.length) {
        let viesItem = this.integrations.filter(integration => integration.type === 'VIES');

        if (viesItem[0]) {
          return viesItem[0].enabled === 1;
        }
      }

      return false;
    },
    invoiceDataNotFromGUS () {
      return this.invoice_country_prefix && this.invoice_country_prefix !== 'PL';
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    }
  },
  watch: {
    allDataLoaded (newValue) {
      if (newValue) {
        if (this.sources.length) {
          this.sourceID = this.sources[0].id;
        }

        if (this.countries.length) {
          this.correspondenceCountryID = this.countries[0].id;
        }
      }
    }
  },
  data() {
    return {
      id: 0,
      name: '',
      code: '',
      email: '',
      phone: '',
      agent_id: null,
      terms_of_sale: '',
      terms_of_sale_indent: '',
      source_id: 0,
      country_id: null,
      region_id: 0,
      logo: '',
      logo_url: '',
      payment_days: '',
      is_supplier: false,
      send_purchase_invoice_email: false,
      purchase_invoice_email: '',
      products_reminder_email: '',
      send_communication: false,
      send_stock_report: false,
      hide_customer_in_qr: false,
      exclude_from_invoice_reminder: false,
      currency_id: null,
      language_id: null,
      shipper_id: null,
      tags: {},
      correspondence_country_id: 0,
      correspondence_name: '',
      correspondence_company_name: '',
      correspondence_street: '',
      correspondence_building: '',
      correspondence_locale: '',
      correspondence_postcode: '',
      correspondence_city: '',
      invoice_receiver: null,
      invoice_name: '',
      invoice_email: '',
      invoice_street: '',
      invoice_building: '',
      invoice_locale: '',
      invoice_postcode: '',
      invoice_city: '',
      invoice_tax_number: '',
      invoice_country_prefix: '',
      discount_services: 0,
      discount_products: 0,
      discount_shipping: 0,
      auto_special_requirement: false,
      specialRequirement: '',
      indent_discount_enabled: false,
      force_update_data_at: null,
      tax_number_verified_at: null,
      tax_number_verified_status: null,
      forceUpdateDataInSoftlabInProgress: false,
      newsletter_status: null,
      newsletter_status_at: null,
      mailing_consent: null,
      loadedData: false,
      loadedSources: false,
      loadedCountries: false,
      loadedIntegrations: false,
      loadedLanguages: false,
      loadedCurrencies: false,
      loadedShippers: false,
      loadedRegions: false,
      countries: [],
      sources: [],
      contactPersons: [],
      addresses: [],
      integrations: [],
      languages: [],
      currencies: [],
      customer_translations: [],
      regions: [],
      shippers: [],
      loadError: false,
      loadedAddresses: false,
      loadAddressesError: false,
      showAddressAddPopup: false,
      showAddressEditPopup: false,
      selectedAddress: false,
      loadedContactPersons: false,
      loadContactPersonsError: false,
      showContactPersonAddPopup: false,
      showContactPersonEditPopup: false,
      selectedContactPerson: false,
      removeAddressInProgress: false,
      removeContactPersonInProgress: false,
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false,
      password: '',
      passwordConfirmation: '',
      passwordChangeInProgress: false,
      purchase_category: null,
      addressFilter: '',
      loadedCustomerTags: false,
      customerTags: [],
      showOrderContactPopup: false,
      showOrderContactEditPopup: false,
      selectedContactID: null,
      checkingTaxNumber: false,
      downloadingTaxIDConfirmationPdf: false,
      latest_verified_tax_number: '',
      tax_number_verified_is_active: false,
      checkedTaxNumberAgain: false,
      warehouse: null,
      can_manually_verify_vies: false,
      manualCheckingTaxNumber: false,
      exceptionOccurred: false,
      // prices
      products: [],
      loadedProducts: false,
      selectedPrice: 0,
      showPricePopup: false,
      showPricesExportPopup: false,
      showPricesImportPopup: false,
      // orders popup
      showCustomerOrdersPopup: false,
      ordersPopupContent: 'orders'
    };
  },
  mounted () {
    let idToLoad = false;
    let endpointUrl = this.getApiEndpoint('/customers/item/');

    if (this.userIsCustomer) {
      idToLoad = 0;
      endpointUrl = this.getApiEndpoint('/customers/item');
    }

    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: endpointUrl,
      instanceFields: ['name', 'code', 'email', 'phone', 'terms_of_sale', 'terms_of_sale_indent', 'source_id', 'country_id', 'region_id', 'logo', 'logo_url', 'payment_days', 'is_supplier', 'products_reminder_email', 'send_purchase_invoice_email', 'purchase_invoice_email', 'send_communication', 'hide_customer_in_qr', 'language_id', 'currency_id', 'shipper_id', 'send_stock_report', 'exclude_from_invoice_reminder', 'correspondence_country_id', 'correspondence_name', 'correspondence_company_name', 'correspondence_street', 'correspondence_building', 'correspondence_locale', 'correspondence_postcode', 'correspondence_city', 'invoice_name', 'invoice_receiver', 'invoice_email', 'invoice_street', 'invoice_building', 'invoice_locale', 'invoice_postcode', 'invoice_city', 'invoice_tax_number', 'invoice_country_prefix', 'discount_services', 'discount_products', 'discount_shipping', 'customer_translations', 'force_update_data_at', 'tax_number_verified_at', 'tax_number_verified_status', 'latest_verified_tax_number', 'tax_number_verified_is_active', 'indent_discount_enabled', 'auto_special_requirement', 'special_requirement', 'purchase_category', 'warehouse', 'agent_id', 'can_manually_verify_vies', 'newsletter_status', 'newsletter_status_at'],
      loadedKey: 'loadedData',
      errorKey: 'loadError',
      overrideID: idToLoad,
      params: {
        with: ['customerTranslations']
      },
      booleanFields: ['is_supplier', 'send_stock_report', 'exclude_from_invoice_reminder', 'send_purchase_invoice_email', 'send_communication', 'hide_customer_in_qr', 'indent_discount_enabled', 'auto_special_requirement'],
      successAction: (response) => {
        if (!this.invoice_receiver) {
          this.invoice_receiver = 'NONE';
        }

        if (!this.userIsCustomer) {
          this.setUpTags(response);
        }

        this.mailing_consent = response.newsletter_status === 'SUBSCRIBED';

        this.loadAdditionalData();
        this.loadAddresses();
        this.loadContactPersons();
      }
    });

    this.$bus.$on('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$on('customer-edit-contact-person-popup-close', this.closeContactPersonEditPopup);
    this.$bus.$on('customer-reload-contact-persons', this.loadContactPersons);
    this.$bus.$on('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$on('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$on('customer-reload-addresses', this.loadAddresses);
    this.$bus.$on('customer-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('customer-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('customer-reload-translations', this.loadTranslations);
    this.$bus.$on('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$on('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$on('close-order-contact-edit-popup', this.closeOrderContactEditPopup);
    this.$bus.$on('show-order-contact-edit-popup', this.openOrderContactEditPopup);
    this.$bus.$on('customer-prices-export-popup-close', this.closeExportPopup);
    this.$bus.$on('customer-prices-import-popup-close', this.closeImportPopup);
    this.$bus.$on('customer-price-popup-open', this.openPricePopup);
    this.$bus.$on('customer-price-popup-close', this.closePricePopup);
    this.$bus.$on('customer-orders-popup-close', this.closeCustomerOrdersPopup);
    this.$bus.$on('ui-select-agent-updated', this.setAgentID);
  },
  methods: {
    setCustomerLogo (event) {
      this.logo = event.target.files[0];
    },
    removeCustomerLogo () {
      this.logo_url = '';
      this.logo = '';
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/countries/items'),
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/languages/items'),
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError',
        successAction: () => {
          this.languages.reverse();

          if (this.language_id === null) {
            this.language_id = this.languages[0].id;
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/shippers/items'),
        loadedKey: 'loadedShippers',
        outputKey: 'shippers',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
        successAction: () => {
          if (this.currency_id === null) {
            this.currency_id = this.currencies[0].id;
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/regions/items',
        loadedKey: 'loadedRegions',
        outputKey: 'regions',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customers/tags/items',
        loadedKey: 'loadedCustomerTags',
        outputKey: 'customerTags',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      if (this.userIsCustomer) {
        this.loadedSources = true;
      } else {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/sources/items'),
          loadedKey: 'loadedSources',
          outputKey: 'sources',
          errorKey: 'loadError'
        });
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/integrations/items',
        loadedKey: 'loadedIntegrations',
        outputKey: 'integrations',
        errorKey: 'loadError'
      });
    },
    loadAddresses () {
      this.loadedAddresses = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/customers/addresses/items'),
        loadedKey: 'loadedAddresses',
        outputKey: 'addresses',
        errorKey: 'loadAddressError',
        params: {
          customer_id: this.id
        }
      });
    },
    loadContactPersons () {
      this.loadedContactPersons = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/customers/contacts/items'),
        loadedKey: 'loadedContactPersons',
        outputKey: 'contactPersons',
        errorKey: 'loadContactPersonsError',
        params: {
          customer_id: this.id
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-edit-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        name: this.name,
        code: this.code,
        email: this.email,
        phone: this.phone,
        terms_of_sale: this.terms_of_sale,
        terms_of_sale_indent: this.terms_of_sale_indent,
        source_id: this.source_id,
        country_id: this.country_id,
        region_id: this.region_id,
        payment_days: this.payment_days,
        is_supplier: +this.is_supplier,
        products_reminder_email: this.products_reminder_email,
        send_purchase_invoice_email: this.send_purchase_invoice_email,
        purchase_invoice_email: this.purchase_invoice_email,
        currency_id: this.currency_id,
        language_id: this.language_id,
        shipper_id: this.shipper_id,
        send_communication: +this.send_communication,
        send_stock_report: +this.send_stock_report,
        hide_customer_in_qr: +this.hide_customer_in_qr,
        exclude_from_invoice_reminder: +this.exclude_from_invoice_reminder,
        correspondence_country_id: this.correspondence_country_id,
        correspondence_name: this.correspondence_name,
        correspondence_company_name: this.correspondence_company_name,
        correspondence_street: this.correspondence_street,
        correspondence_building: this.correspondence_building,
        correspondence_locale: this.correspondence_locale,
        correspondence_postcode: this.correspondence_postcode,
        correspondence_city: this.correspondence_city,
        invoice_receiver: this.invoice_receiver,
        invoice_name: this.invoice_name,
        invoice_email: this.invoice_email,
        invoice_street: this.invoice_street,
        invoice_building: this.invoice_building,
        invoice_locale: this.invoice_locale,
        invoice_postcode: this.invoice_postcode,
        invoice_city: this.invoice_city,
        invoice_tax_number: this.invoice_tax_number,
        invoice_country_prefix: this.invoice_country_prefix,
        discount_services: this.prepareDiscount(this.discount_services),
        discount_products: this.prepareDiscount(this.discount_products),
        discount_shipping: this.prepareDiscount(this.discount_shipping),
        indent_discount_enabled: this.indent_discount_enabled,
        auto_special_requirement: this.auto_special_requirement,
        special_requirement: this.auto_special_requirement ? this.special_requirement : '',
        mailing_consent: this.mailing_consent
      };

      if (this.userIsAdmin) {
        formDataToSend.agent_id = this.agent_id;
      }

      if (this.is_supplier) {
        formDataToSend.purchase_category = this.purchase_category;
        formDataToSend.warehouse = this.warehouse;
      }

      if (!this.userIsCustomer) {
        formDataToSend.tags = this.prepareTags();
      }

      if (this.logo) {
        formDataToSend.logo = this.logo;
        imageFields = ['logo'];
      }

      let endpointUrl = this.getApiEndpoint('/customers/update/' + this.id);

      if (this.userIsCustomer) {
        endpointUrl = this.getApiEndpoint('/customers/update');
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('customer.editClientSuccessTitle'),
        successText: this.$t('customer.editClientSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.editClientErrorText'),
        successAction: () => {
          let idToLoad = false;
          let loadEndpointUrl = '/customers/item/';

          if (this.userIsCustomer) {
            idToLoad = 0;
            loadEndpointUrl = this.getApiEndpoint('/customers/item');
          }

          if (this.language_id && this.userIsCustomer) {
            this.$store.commit('setUserLang', parseInt(this.language_id, 10));
          }

          FormUtils.loadItemData(this, {
            method: 'get',
            endpointBase: loadEndpointUrl,
            overrideID: idToLoad,
            instanceFields: ['logo', 'logo_url', 'auto_special_requirement', 'special_requirement', 'newsletter_status', 'newsletter_status_at'],
            booleanFields: ['auto_special_requirement'],
            loadedKey: 'loadedData',
            errorKey: 'loadError'
          });
          this.loadAddresses();
          this.loadContactPersons();
        }
      }, imageFields);
    },
    /**
     * Addresses
     */
    addAddress () {
      this.showAddressAddPopup = true;
    },
    editAddress (address) {
      this.selectedAddress = JSON.parse(JSON.stringify(address));
      this.showAddressEditPopup = true;
    },
    removeAddress (addressID) {
      this.removeAddressInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/customers/addresses/delete/'),
        id: addressID,
        successTitle: this.$t('customer.deleteAddressSuccessTitle'),
        successText: this.$t('customer.deleteAddressSuccessText'),
        confirmTitle: this.$t('customer.deleteAddressConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.deleteAddressErrorText'),
        successAction: () => {
          this.removeAddressInProgress = false;
          this.loadAddresses();
        },
        cancelAction: () => {
          this.removeAddressInProgress = false;
        }
      });
    },
    closeAddressAddPopup () {
      this.showAddressAddPopup = false;
    },
    closeAddressEditPopup () {
      this.showAddressEditPopup = false;
    },
    /**
     * Contact persons
     */
    addContactPerson () {
      this.showContactPersonAddPopup = true;
    },
    editContactPerson (contactPerson) {
      this.selectedContactPerson = JSON.parse(JSON.stringify(contactPerson));
      this.showContactPersonEditPopup = true;
    },
    removeContactPerson (contactPersonID) {
      this.removeContactPersonInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/customers/contacts/delete/'),
        id: contactPersonID,
        successTitle: this.$t('customer.deleteContactPersonSuccessTitle'),
        successText: this.$t('customer.deleteContactPersonSuccessText'),
        confirmTitle: this.$t('customer.deleteContactPersonConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.deleteContactPersonErrorText'),
        successAction: () => {
          this.removeContactPersonInProgress = false;
          this.loadContactPersons();
        }
      });
    },
    closeContactPersonAddPopup () {
      this.showContactPersonAddPopup = false;
    },
    closeContactPersonEditPopup () {
      this.showContactPersonEditPopup = false;
    },
    getCountry (countryID) {
      return CommonGetters.getCountryById(countryID, this.countries);
    },
    /**
     * Translations
     */
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: this.getApiEndpoint('/customers/item/'),
        instanceFields: ['customer_translations'],
        loadedKey: null,
        params: {
          with: ['customerTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: this.getApiEndpoint('/customers/translations/delete/'),
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    },
    validatePasswordChangeForm () {
      FormUtils.validate(this.$refs['customer-password-change'], this.changePassword);
    },
    changePassword () {
      this.passwordChangeInProgress = true;
      let formDataToSend = {
        password: this.password,
        password_confirmation: this.passwordConfirmation
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-panel/users/change-password',
        formData: formDataToSend,
        successTitle: this.$t('customer.editPasswordSuccessTitle'),
        successText: this.$t('customer.editPasswordSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.editPasswordErrorText'),
        successAction: () => {
          this.passwordChangeInProgress = false;
        },
        failAction: () => {
          this.passwordChangeInProgress = false;
        }
      });
    },
    prepareDiscount (value) {
      if (Number.isNaN(value) || (typeof value === 'string' && value.trim() === '')) {
        return 0;
      }

      if (typeof value === 'string') {
        value = parseFloat(value.replace(',', '.'));
      }

      return value;
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    setUpTags (response) {
      for (let i = 0; i < response.tags.length; i++) {
        let id = response.tags[i].id;
        this.tags[id] = true;
      }
    },
    prepareTags () {
      let output = [];
      let tagIDs = Object.keys(this.tags);

      for (let i = 0; i < tagIDs.length; i++) {
        if (this.tags[tagIDs[i]]) {
          output.push(tagIDs[i]);
        }
      }

      return output;
    },
    forceUpdateDataInSoftlab () {
      this.forceUpdateDataInSoftlabInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/customers/force-update-data/' + this.id,
        formData: {},
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('softlab.forceUpdateErrorText'),
        successAction: () => {
          this.forceUpdateDataInSoftlabInProgress = false;
          this.force_update_data_at = true;

          Vue.swal({
            title: this.$t('softlab.forceUpdateSuccessTitle'),
            html: '',
            type: 'success',
            timer: 2000,
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        },
        failAction: () => {
          this.forceUpdateDataInSoftlabInProgress = false;
        }
      });
    },
    openOrderContactPopup () {
      this.showOrderContactPopup = true;
    },
    closeOrderContactPopup () {
      this.showOrderContactPopup = false;
    },
    openOrderContactEditPopup (contactID) {
      this.showOrderContactEditPopup = true;
      this.selectedContactID = contactID;
    },
    closeOrderContactEditPopup () {
      this.showOrderContactEditPopup = false;
      this.selectedContactID = null;
    },
    verifyCustomerTaxID () {
      if (!this.invoice_country_prefix) {
        Vue.swal({
          title: this.$t('customer.youMustSelectCountryPrefix'),
          html: this.$t('customer.youMustSelectCountryPrefixDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.checkingTaxNumber = true;
      this.checkedTaxNumberAgain = true;

      this.$http.get('/customers/verify-tax-number', {
        params: {
          id: parseInt(this.$route.params.id, 10),
          prefix: this.invoice_country_prefix,
          number: this.invoice_tax_number
        }
      }).then(response => {
        this.checkingTaxNumber = false;

        if (response.data && response.data.verification && response.data.verification.is_exception) {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('customer.viesCommunicationIssue'),
            type: 'error',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.exceptionOccurred = true;
        } else if (response.data.success) {
          if (response.data.verification && response.data.verification.status === 'VALID') {
            if (this.invoice_country_prefix === 'PL') {
              let previousInvoiceData = [
                this.invoice_city,
                this.invoice_name,
                this.invoice_postcode,
                this.invoice_street,
                this.invoice_building,
                this.invoice_locale
              ].join('|');

              this.invoice_city = response.data.verification.response.city;
              this.invoice_name = response.data.verification.response.name;
              this.invoice_postcode = response.data.verification.response.postCode;

              if (this.invoice_postcode && this.invoice_country_prefix === 'PL') {
                this.invoice_postcode = this.invoice_postcode.substr(0, 2) + '-' + this.invoice_postcode.substr(2, 3);
              }

              this.invoice_street = response.data.verification.response.street;

              if (this.invoice_street && this.invoice_street.substr(0, 4) === 'ul. ') {
                this.invoice_street = this.invoice_street.replace('ul. ', '');
              }

              this.invoice_building = response.data.verification.response.streetNumber;
              this.invoice_locale = response.data.verification.response.houseNumber;

              let newInvoiceData = [
                this.invoice_city,
                this.invoice_name,
                this.invoice_postcode,
                this.invoice_street,
                this.invoice_building,
                this.invoice_locale
              ].join('|');

              let previousCorrespondenceData = [
                this.correspondence_country_id,
                this.correspondence_city,
                this.correspondence_company_name,
                this.correspondence_postcode,
                this.correspondence_street,
                this.correspondence_building,
                this.correspondence_locale
              ].join('|');

              let newCorrespondenceData = [
                parseInt(this.polandCountryID, 10),
                this.invoice_city,
                this.invoice_name,
                this.invoice_postcode,
                this.invoice_street,
                this.invoice_building,
                this.invoice_locale
              ].join('|');

              if (previousCorrespondenceData !== newCorrespondenceData) {
                this.correspondence_country_id = parseInt(this.polandCountryID, 10);
                this.correspondence_city = this.invoice_city;
                this.correspondence_company_name = this.invoice_name;
                this.correspondence_postcode = this.invoice_postcode;
                this.correspondence_street = this.invoice_street;
                this.correspondence_building = this.invoice_building;
                this.correspondence_locale = this.invoice_locale;
              }

              if (previousCorrespondenceData !== newCorrespondenceData && previousInvoiceData !== newInvoiceData) {
                Vue.swal({
                  title: this.$t('customer.invoiceAndCorrespondenceAddressSet'),
                  html: this.$t('customer.invoiceAndCorrespondenceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                }).then(() => {
                  if (!this.addresses.length) {
                    this.addAddressBasedOnTaxInfo();
                  }
                });
              } else if (previousCorrespondenceData !== newCorrespondenceData) {
                Vue.swal({
                  title: this.$t('customer.correspondenceAddressSet'),
                  html: this.$t('customer.correspondenceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                }).then(() => {
                  if (!this.addresses.length) {
                    this.addAddressBasedOnTaxInfo();
                  }
                });
              } else if (previousInvoiceData !== newInvoiceData) {
                Vue.swal({
                  title: this.$t('customer.invoiceAddressSet'),
                  html: this.$t('customer.invoiceAddressSetDesc'),
                  type: 'info',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                }).then(() => {
                  if (!this.addresses.length) {
                    this.addAddressBasedOnTaxInfo();
                  }
                });
              }
            } else if (!this.addresses.length) {
              this.addAddressBasedOnTaxInfo();
            }
          } else if (response.data.integration_disabled === 1) {
            Vue.swal({
              title: this.$t('customer.verificationIntegrationDisabledTitle'),
              html: this.$t('customer.verificationIntegrationDisabledDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          } else if (response.data.verification_skipped !== 1) {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('customer.invalidTaxNumberLong'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            if (this.invoice_country_prefix === 'PL') {
              this.invoice_city = '';
              this.invoice_name = '';
              this.invoice_postcode = '';
              this.invoice_street = '';
              this.invoice_building = '';
              this.invoice_locale = '';
            }
          }

          if (response.data.verification_skipped !== 1) {
            this.tax_number_verified_at = response.data.verification.created_at;
            this.tax_number_verified_status = response.data.verification.status;
            this.latest_verified_tax_number = this.invoice_country_prefix + '' + this.invoice_tax_number;
            this.tax_number_verified_is_active = response.data.verification.is_active;
          }
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.checkingTaxNumber = false;
      });
    },
    manuallyVerifyVies () {
      this.manualCheckingTaxNumber = true;

      this.$http.post('/customers/manually-verify-vies/' + this.customerID, {}).then(response => {
        if (response.data && response.data.success) {
          Vue.swal({
            title: this.$t('customer.viesVerifiedManuallyTitle'),
            html: this.$t('customer.viesVerifiedManuallyDesc'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          }).then(() => {
            window.location.reload();
          });
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }

        this.manualCheckingTaxNumber = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        this.manualCheckingTaxNumber = false;
      });
    },
    downloadTaxNumberConfirmation () {
      this.downloadingTaxIDConfirmationPdf = true;

      this.$http.post('/customers/download-tax-number-verification/' + this.customerID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('customer.taxNumberConfirmationFileNamePrefix');
        link.setAttribute('download', filenamePrefix + this.customerID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingTaxIDConfirmationPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingTaxIDConfirmationPdf = false;
      });
    },
    /**
     * Prices
     */
    openPricePopup (priceData = {}) {
      this.showPricePopup = true;
      this.selectedPrice = priceData;
    },
    openExportPopup () {
      this.showPricesExportPopup = true;
    },
    openImportPopup () {
      this.showPricesImportPopup = true;
    },
    closePricePopup () {
      this.showPricePopup = false;
    },
    closeExportPopup () {
      this.showPricesExportPopup = false;
    },
    closeImportPopup () {
      this.showPricesImportPopup = false;
    },
    addAddressBasedOnTaxInfo () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: this.getApiEndpoint('/customers/addresses/store'),
        formData: {
          customer_id: this.id,
          name: (this.name).toString().toLocaleUpperCase(),
          email: (this.email).toString().toLocaleUpperCase(),
          company_name: (this.invoice_name).toString().toLocaleUpperCase(),
          country_id: parseInt(this.polandCountryID, 10),
          street: (this.invoice_street).toString().toLocaleUpperCase(),
          building: (this.invoice_building).toString().toLocaleUpperCase(),
          locale: (this.invoice_locale).toString().toLocaleUpperCase(),
          postcode: (this.invoice_postcode).toString().toLocaleUpperCase(),
          city: (this.invoice_city).toString().toLocaleUpperCase(),
          phone: this.phone
        },
        successTitle: this.$t('customer.addAddressByTaxInfoSuccessTitle'),
        successText: this.$t('customer.addAddressByTaxInfoSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.addAddressErrorText'),
        successAction: () => {
          this.$bus.$emit('customer-reload-addresses');
        }
      });
    },
    countryChange () {
      Vue.nextTick(() => {
        let countryData = this.countries.filter(country => country.id === this.country_id);

        if (countryData[0]) {
          let currencyID = countryData[0].default_currency_id;
          let languageID = countryData[0].default_language_id;

          if (currencyID) {
            Vue.set(this, 'currency_id', currencyID);
          } else {
            Vue.set(this, 'currency_id', null);
          }

          if (languageID) {
            Vue.set(this, 'language_id', languageID);
          } else {
            Vue.set(this, 'language_id', null);
          }
        }
      });
    },
    showCustomerOrders () {
      this.ordersPopupContent = 'orders';

      Vue.nextTick(() => {
        this.showCustomerOrdersPopup = true;
      });
    },
    showCustomerOffers () {
      this.ordersPopupContent = 'offers';

      Vue.nextTick(() => {
        this.showCustomerOrdersPopup = true;
      });
    },
    closeCustomerOrdersPopup () {
      this.showCustomerOrdersPopup = false;
    },
    setInvoiceDataAsCorrespondenceData () {
      this.correspondence_country_id = CommonGetters.getCountryIdByCode(this.invoice_country_prefix, this.countries);
      this.correspondence_city = this.invoice_city;
      this.correspondence_company_name = this.invoice_name;
      this.correspondence_postcode = this.invoice_postcode;
      this.correspondence_street = this.invoice_street;
      this.correspondence_building = this.invoice_building;
      this.correspondence_locale = this.invoice_locale;

      Vue.swal({
        title: this.$t('customer.correspondenceAddressSet'),
        html: '',
        type: 'success',
        timer: 2000,
        confirmButtonText: this.$t('ui.ok'),
        buttonsStyling: true
      });
    },
    setAgentID (agentData) {
      this.agent_id = agentData.agentID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$off('customer-edit-contact-person-popup-close', this.closeContactPersonEditPopup);
    this.$bus.$off('customer-reload-contact-persons', this.loadContactPersons);
    this.$bus.$off('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$off('customer-edit-address-popup-close', this.closeAddressEditPopup);
    this.$bus.$off('customer-reload-addresses', this.loadAddresses);
    this.$bus.$off('customer-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('customer-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('customer-reload-translations', this.loadTranslations);
    this.$bus.$off('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$off('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$off('close-order-contact-edit-popup', this.closeOrderContactEditPopup);
    this.$bus.$off('show-order-contact-edit-popup', this.openOrderContactEditPopup);
    this.$bus.$off('customer-prices-export-popup-close', this.closeExportPopup);
    this.$bus.$off('customer-prices-import-popup-close', this.closeImportPopup);
    this.$bus.$off('customer-price-popup-open', this.openPricePopup);
    this.$bus.$off('customer-price-popup-close', this.closePricePopup);
    this.$bus.$off('customer-orders-popup-close', this.closeCustomerOrdersPopup);
    this.$bus.$off('ui-select-agent-updated', this.setAgentID);
  }
};
</script>

<style lang="scss" scoped>
.customer-logo {
  img {
    height: auto;
    width: 120px;
  }
}

.customer-tags-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  list-style-type: none;
  margin: 20px 0 0 0;
  padding: 20px 20px 10px 20px;

  li {
    font-size: 12px;
  }
}
</style>
